import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - Redesigned for Impact */}
      <section 
        className="relative min-h-[90vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/b0fd27b8640f87620727fc823762028b.jpg')`
        }}
      >
        {/* Multi-layer overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/85 via-orange-800/70 to-gray-900/60"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Main content - takes 3 cols */}
            <div className="lg:col-span-3 space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-white/90 text-sm font-medium">Gratis begeleiding in heel Nederland</span>
              </div>
              
              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1]">
                Een nieuwe start voor jongeren die{' '}
                <span className="text-orange-300">
                  hun weg terugvinden
                </span>
              </h1>
              
              {/* Subheadline */}
              <p className="text-lg sm:text-xl text-white/80 max-w-xl leading-relaxed">
                HerStart begeleidt thuiszittende jongeren met empathie en deskundigheid. 
                Van herstel naar groei, naar een hoopvolle toekomst in het onderwijs.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a
                  href="/contact?onderwerp=hulp"
                  className="group inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:-translate-y-0.5"
                >
                  Start een gesprek
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </a>
                <a
                  href="/onze-aanpak"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200"
                >
                  <i className="ri-play-circle-line"></i>
                  Ontdek onze aanpak
                </a>
              </div>
              
              {/* Trust indicators */}
              <div className="flex flex-wrap items-center gap-6 pt-6 text-white/70 text-sm">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-green-400"></i>
                  <span>Bewezen aanpak</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-heart-line text-orange-400"></i>
                  <span>Empathische begeleiding</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-graduation-cap-line text-yellow-400"></i>
                  <span>Duurzame resultaten</span>
                </div>
              </div>
            </div>
            
            {/* Stats card - takes 2 cols */}
            <div className="lg:col-span-2">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-8">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-4xl sm:text-5xl font-bold text-white mb-1">500+</div>
                    <div className="text-white/70 text-sm">Jongeren geholpen</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl sm:text-5xl font-bold text-orange-300 mb-1">85%</div>
                    <div className="text-white/70 text-sm">Terug naar school</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl sm:text-5xl font-bold text-green-300 mb-1">25+</div>
                    <div className="text-white/70 text-sm">Samenwerkingspartners</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl sm:text-5xl font-bold text-white mb-1">10+</div>
                    <div className="text-white/70 text-sm">Jaar ervaring</div>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="text-white/80 text-center text-sm">
                    "Door HerStart heb ik weer vertrouwen in de toekomst"
                  </p>
                  <p className="text-white/60 text-center text-xs mt-2">— Sarah, 16 jaar</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50">
          <span className="text-xs uppercase tracking-wider">Scroll</span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Introductie Section */}
      <section className="py-20 bg-orange-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom HerStart?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Wij geloven dat elke jongere het recht heeft op onderwijs en een hoopvolle toekomst. 
              Met onze persoonlijke begeleiding helpen we jongeren hun motivatie en zelfvertrouwen terug te vinden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-heart-line text-2xl text-orange-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Empathische begeleiding</h3>
              <p className="text-gray-600">
                Onze ervaren begeleiders bieden een veilige en warme omgeving waar jongeren zich gehoord en begrepen voelen.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-plant-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Persoonlijke groei</h3>
              <p className="text-gray-600">
                We focussen op het herstel van zelfvertrouwen en motivatie, zodat jongeren weer geloven in hun eigen mogelijkheden.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-graduation-cap-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Duurzame terugkeer</h3>
              <p className="text-gray-600">
                Onze aanpak zorgt voor een stabiele en succesvolle terugkeer naar het onderwijs, met blijvende resultaten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Onze Aanpak Preview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Onze bewezen aanpak
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Met onze drieledige aanpak begeleiden we jongeren stap voor stap terug naar het onderwijs. 
                Van herstel naar groei, en van groei naar een hoopvolle toekomst.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-orange-500 font-semibold">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Herstel</h4>
                    <p className="text-gray-600">Het hervinden van balans en vertrouwen in een veilige omgeving.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-green-600 font-semibold">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Groei</h4>
                    <p className="text-gray-600">Het versterken van motivatie en ontwikkeling van levensvaardigheden.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-yellow-600 font-semibold">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Toekomst</h4>
                    <p className="text-gray-600">Het bouwen aan duurzame schooldeelname en toekomstperspectief.</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <Button href="/onze-aanpak">
                  Meer over onze aanpak
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Young%20diverse%20students%20in%20a%20supportive%20learning%20environment%2C%20mentor%20guiding%20teenagers%20in%20a%20bright%20modern%20classroom%2C%20growth%20and%20development%20theme%2C%20soft%20natural%20lighting%2C%20inspiring%20educational%20setting%20with%20plants%20and%20books%2C%20warm%20and%20hopeful%20atmosphere&width=600&height=400&seq=aanpak-preview&orientation=landscape"
                alt="Begeleiding van jongeren in een ondersteunende leeromgeving"
                className="rounded-xl shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Training Highlight */}
      <section className="py-20 bg-green-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Sterk in je Zelf Training
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Een gezond zelfbeeld vormt de basis voor leren, motivatie en sociaal functioneren. 
              Onze bewezen training helpt jongeren hun kwaliteiten te herkennen en waarderen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-eye-line text-xl text-orange-500"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Kwaliteiten herkennen</h4>
              <p className="text-sm text-gray-600">Leerlingen ontdekken hun sterke punten en talenten</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-refresh-line text-xl text-green-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Positieve gedachten</h4>
              <p className="text-sm text-gray-600">Negatieve gedachten vervangen door helpende gedachten</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-trophy-line text-xl text-yellow-600"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Succeservaringen</h4>
              <p className="text-sm text-gray-600">Bewust oproepen en verankeren van positieve ervaringen</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-chat-smile-line text-xl text-orange-500"></i>
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Positief gedrag</h4>
              <p className="text-sm text-gray-600">Zelfspraak en gedrag in de praktijk brengen</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button href="/trainingen" variant="secondary">
              Meer over onze trainingen
            </Button>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Samen geven we jongeren een nieuwe start
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
            Ben je een gemeente, school, ouder of wil je op een andere manier bijdragen aan onze missie? 
            We horen graag van je en bespreken graag de mogelijkheden.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/doe-mee" variant="outline" className="bg-white text-orange-500 hover:bg-gray-50 border-white">
              Sluit je aan bij HerStart
            </Button>
            <Button href="/contact?onderwerp=anders" className="bg-green-600 hover:bg-green-700">
              Neem contact op
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
